import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const loggedUser = await login(email, password, remember);
      navigate(loggedUser.must_change_password === true ? "/change-password" : "/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async () => {
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.info("Il recupero password pubblico è temporaneamente disabilitato. Contatta un amministratore autorizzato.");
      setShowForgot(false);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="lg:w-1/2 bg-slate-900 text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-[#E30613]" />
        <div>
          <div className="font-display text-3xl font-extrabold">
            <span className="text-[#E30613]">COSMA</span> TRAVEL DESK
          </div>
          <div className="text-sm tracking-[0.3em] text-slate-400 mt-1">GESTIONE TRASFERTE AZIENDALI</div>
        </div>
        <div className="hidden lg:block max-w-md">
          <Plane size={48} className="text-[#E30613] mb-6" />
          <h2 className="font-display text-3xl font-bold leading-tight">
            Organizza e gestisci le trasferte di COSMA e RIMAR in modo semplice e veloce.
          </h2>
          <p className="text-slate-400 mt-4">
            Un unico strumento per pianificare trasporti, hotel, prenotazioni, preventivi e itinerari.
          </p>
        </div>
        <div className="text-xs text-slate-500">© {new Date().getFullYear()} COSMA — Strumento interno aziendale</div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-slate-900">Accedi</h1>
          <p className="text-slate-500 mt-1 mb-8">Inserisci le tue credenziali aziendali.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@cosma.it" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="login-remember" checked={remember} onCheckedChange={(c) => setRemember(!!c)} />
                <span className="text-sm text-slate-600">Ricordami</span>
              </label>
              <button type="button" onClick={() => setShowForgot(true)} data-testid="forgot-password-link" className="text-sm text-[#E30613] font-medium hover:underline">
                Password dimenticata?
              </button>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-[#E30613] hover:bg-[#C50510] text-white font-semibold h-11">
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>

        </div>
      </div>

      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent>
          <DialogHeader><DialogTitle>Recupero password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Email</Label>
            <Input data-testid="forgot-email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nome@cosma.it" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForgot(false)}>Annulla</Button>
            <Button data-testid="forgot-submit" onClick={sendForgot} className="bg-[#E30613] hover:bg-[#C50510]">Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
